/**
 * Swedish Tax Agency (Skatteverket) API Service
 * 
 * This service integrates with Skatteverket's public API to calculate
 * preliminary tax (preliminär skatt) based on gross salary.
 * 
 * API Documentation: https://skatteverket.entryscape.net/
 * 
 * Tax Tables (tabellnr):
 * - Table 30: Standard tax table for employees without tax deductions
 * - Table 29: For those with tax deductions
 * - Table 31: For those with higher municipal tax
 * - Table 32: For those with church tax
 * - Table 33: For those age 65+ without tax deductions
 * 
 * Columns explanation:
 * - kolumn 1: Total preliminary tax (most commonly used)
 * - kolumn 2: State income tax
 * - kolumn 3: Municipal tax
 * - kolumn 4: Burial fee
 * - kolumn 5: Sum of columns 2-4
 * - kolumn 6: Tax reduction for work income
 * - kolumn 7: (Reserved for future use)
 */

const SKATTEVERKET_API_BASE = 'https://skatteverket.entryscape.net/rowstore/dataset/88320397-5c32-4c16-ae79-d36d95b17b95'

/**
 * Tax table types
 */
export const TaxTables = {
  STANDARD: '30',              // Standard table for employees
  WITH_DEDUCTIONS: '29',       // For those with tax deductions
  HIGH_MUNICIPAL: '31',        // Higher municipal tax
  WITH_CHURCH: '32',           // Including church tax
  SENIOR: '33',                // Age 65+ without deductions
  SENIOR_WITH_DEDUCTIONS: '34' // Age 65+ with deductions
}

/**
 * Payment period types
 */
export const PaymentPeriods = {
  MONTHLY: '30B',              // Monthly (30 days basis)
  WEEKLY: '7B',                // Weekly (7 days basis)
  BIWEEKLY: '14B',             // Bi-weekly (14 days basis)
  DAILY: '1B'                  // Daily (1 day basis)
}

/**
 * Fetch tax data from Skatteverket API
 * 
 * @param {Object} params - Query parameters
 * @param {string} params.år - Year (e.g., '2025')
 * @param {string} params.tabellnr - Tax table number
 * @param {string} params.antal_dgr - Payment period (e.g., '30B' for monthly)
 * @param {number} params.income - Gross income amount
 * @returns {Promise<Object>} Tax data from API
 */
async function fetchTaxData(params) {
  try {
    const queryParams = new URLSearchParams()
    
    // Add required parameters
    if (params.år) queryParams.append('år', params.år)
    if (params.tabellnr) queryParams.append('tabellnr', params.tabellnr)
    if (params.antal_dgr) queryParams.append('antal dgr', params.antal_dgr)
    
    // Add income range if specified
    if (params.income) {
      // Find the bracket that contains this income
      // We'll search for entries where income falls between "inkomst fr.o.m." and "inkomst t.o.m."
      queryParams.append('_limit', '500')
    }
    
    const url = `${SKATTEVERKET_API_BASE}/json?${queryParams.toString()}`
    
    const response = await fetch(url)
    
    if (!response.ok) {
      throw new Error(`Skatteverket API error: ${response.status} ${response.statusText}`)
    }
    
    const data = await response.json()
    return data
  } catch (error) {
    console.error('Error fetching tax data from Skatteverket:', error)
    throw error
  }
}

/**
 * Calculate preliminary tax for a given gross salary
 * 
 * @param {Object} options - Calculation options
 * @param {number} options.grossSalary - Gross monthly salary
 * @param {string} options.year - Tax year (default: current year)
 * @param {string} options.taxTable - Tax table number (default: '30' - standard)
 * @param {string} options.paymentPeriod - Payment period (default: '30B' - monthly)
 * @returns {Promise<Object>} Tax calculation result
 */
export async function calculatePreliminaryTax({
  grossSalary,
  year = new Date().getFullYear().toString(),
  taxTable = TaxTables.STANDARD,
  paymentPeriod = PaymentPeriods.MONTHLY
}) {
  try {
    // Round gross salary to nearest whole number (API works with integers)
    const roundedSalary = Math.round(grossSalary)
    
    // Fetch tax data for the specified year and table
    const taxData = await fetchTaxData({
      år: year,
      tabellnr: taxTable,
      antal_dgr: paymentPeriod,
      income: roundedSalary
    })
    
    if (!taxData.results || taxData.results.length === 0) {
      throw new Error('No tax data found for the specified parameters')
    }
    
    // Find the correct tax bracket
    const taxBracket = findTaxBracket(taxData.results, roundedSalary)
    
    if (!taxBracket) {
      throw new Error(`No tax bracket found for income ${roundedSalary}`)
    }
    
    // Parse tax amounts (they're stored as strings in the API)
    const preliminaryTax = parseFloat(taxBracket['kolumn 1']) || 0
    const stateTax = parseFloat(taxBracket['kolumn 2']) || 0
    const municipalTax = parseFloat(taxBracket['kolumn 3']) || 0
    const burialFee = parseFloat(taxBracket['kolumn 4']) || 0
    const taxSum = parseFloat(taxBracket['kolumn 5']) || 0
    const taxReduction = parseFloat(taxBracket['kolumn 6']) || 0
    
    // Calculate net salary
    const netSalary = roundedSalary - preliminaryTax
    
    // Calculate effective tax rate
    const effectiveTaxRate = (preliminaryTax / roundedSalary) * 100
    
    return {
      grossSalary: roundedSalary,
      netSalary: netSalary,
      preliminaryTax: preliminaryTax,
      effectiveTaxRate: effectiveTaxRate.toFixed(2),
      breakdown: {
        stateTax: stateTax,
        municipalTax: municipalTax,
        burialFee: burialFee,
        taxSum: taxSum,
        taxReduction: taxReduction
      },
      taxBracket: {
        from: parseInt(taxBracket['inkomst fr.o.m.']),
        to: parseInt(taxBracket['inkomst t.o.m.'])
      },
      metadata: {
        year: year,
        taxTable: taxTable,
        paymentPeriod: paymentPeriod
      }
    }
  } catch (error) {
    console.error('Error calculating preliminary tax:', error)
    throw error
  }
}

/**
 * Find the appropriate tax bracket for a given income
 * 
 * @param {Array} taxBrackets - Array of tax bracket objects from API
 * @param {number} income - Gross income amount
 * @returns {Object|null} Matching tax bracket or null
 */
function findTaxBracket(taxBrackets, income) {
  return taxBrackets.find(bracket => {
    const from = parseInt(bracket['inkomst fr.o.m.'])
    const to = parseInt(bracket['inkomst t.o.m.'])
    return income >= from && income <= to
  })
}

/**
 * Get available tax tables for a given year
 * 
 * @param {string} year - Tax year
 * @returns {Promise<Array>} Available tax tables
 */
export async function getAvailableTaxTables(year = new Date().getFullYear().toString()) {
  try {
    const taxData = await fetchTaxData({
      år: year,
      _limit: 500
    })
    
    // Extract unique table numbers
    const tables = [...new Set(taxData.results.map(item => item.tabellnr))]
      .filter(Boolean)
      .sort()
    
    return tables
  } catch (error) {
    console.error('Error fetching available tax tables:', error)
    throw error
  }
}

/**
 * Calculate tax for multiple salary amounts (batch calculation)
 * 
 * @param {Object} options - Calculation options
 * @param {Array<number>} options.salaries - Array of gross salaries
 * @param {string} options.year - Tax year
 * @param {string} options.taxTable - Tax table number
 * @param {string} options.paymentPeriod - Payment period
 * @returns {Promise<Array>} Array of tax calculation results
 */
export async function calculateBatchTaxes({
  salaries,
  year = new Date().getFullYear().toString(),
  taxTable = TaxTables.STANDARD,
  paymentPeriod = PaymentPeriods.MONTHLY
}) {
  try {
    // Fetch tax data once for all calculations
    const taxData = await fetchTaxData({
      år: year,
      tabellnr: taxTable,
      antal_dgr: paymentPeriod
    })
    
    if (!taxData.results || taxData.results.length === 0) {
      throw new Error('No tax data found for the specified parameters')
    }
    
    // Calculate tax for each salary
    return salaries.map(salary => {
      const roundedSalary = Math.round(salary)
      const taxBracket = findTaxBracket(taxData.results, roundedSalary)
      
      if (!taxBracket) {
        return {
          grossSalary: roundedSalary,
          error: 'No tax bracket found'
        }
      }
      
      const preliminaryTax = parseFloat(taxBracket['kolumn 1']) || 0
      const netSalary = roundedSalary - preliminaryTax
      const effectiveTaxRate = (preliminaryTax / roundedSalary) * 100
      
      return {
        grossSalary: roundedSalary,
        netSalary: netSalary,
        preliminaryTax: preliminaryTax,
        effectiveTaxRate: effectiveTaxRate.toFixed(2)
      }
    })
  } catch (error) {
    console.error('Error in batch tax calculation:', error)
    throw error
  }
}

/**
 * Get tax information for display
 * Useful for showing tax table details to users
 * 
 * @param {string} year - Tax year
 * @param {string} taxTable - Tax table number
 * @param {string} paymentPeriod - Payment period
 * @returns {Promise<Object>} Tax table information
 */
export async function getTaxTableInfo({
  year = new Date().getFullYear().toString(),
  taxTable = TaxTables.STANDARD,
  paymentPeriod = PaymentPeriods.MONTHLY
}) {
  try {
    const taxData = await fetchTaxData({
      år: year,
      tabellnr: taxTable,
      antal_dgr: paymentPeriod,
      _limit: 10
    })
    
    if (!taxData.results || taxData.results.length === 0) {
      throw new Error('No tax data found')
    }
    
    const firstBracket = taxData.results[0]
    
    return {
      year: firstBracket['år'],
      tableNumber: firstBracket['tabellnr'],
      paymentPeriod: firstBracket['antal dgr'],
      totalBrackets: taxData.resultCount,
      description: getTaxTableDescription(taxTable)
    }
  } catch (error) {
    console.error('Error fetching tax table info:', error)
    throw error
  }
}

/**
 * Get human-readable description of tax table
 * 
 * @param {string} tableNumber - Tax table number
 * @returns {string} Description
 */
function getTaxTableDescription(tableNumber) {
  const descriptions = {
    '29': 'With tax deductions (Med avdrag)',
    '30': 'Standard table (Standardtabell)',
    '31': 'Higher municipal tax (Högre kommunalskatt)',
    '32': 'Including church tax (Med kyrkoskatt)',
    '33': 'Age 65+ without deductions (65+ utan avdrag)',
    '34': 'Age 65+ with deductions (65+ med avdrag)'
  }
  
  return descriptions[tableNumber] || 'Unknown table'
}

export default {
  calculatePreliminaryTax,
  calculateBatchTaxes,
  getAvailableTaxTables,
  getTaxTableInfo,
  TaxTables,
  PaymentPeriods
}
